import { createContext, type ReactNode, useContext, useMemo } from 'react';

import type { AnalyticalVariable } from '@verantos-dev/types-cohorts';
// OR-6018: `ConceptGroupsByDomain` was promoted to the shared
// @verantos-dev/ui-concept-group-editor lib alongside the concept-group
// picker / domain filter. Re-exported here so existing in-feature imports stay
// unchanged.
import type {
  ConceptGroupOption,
  ConceptGroupsByDomain,
} from '@verantos-dev/ui-concept-group-editor';

export type { ConceptGroupsByDomain } from '@verantos-dev/ui-concept-group-editor';

export interface AnalyticalVariableCardContextValue {
  analyticalVariable: AnalyticalVariable;
  conceptGroups: ConceptGroupOption[];
  conceptGroupsByDomain: ConceptGroupsByDomain;
  isEditable: boolean;
  onAnalyticalVariableChange?: (updates: Partial<AnalyticalVariable>) => void;
  onDrillDownClick?: (analyticalVariableId: string) => void;
}

const AnalyticalVariableCardContext = createContext<AnalyticalVariableCardContextValue | null>(
  null
);

export const useAnalyticalVariableCardContext = (): AnalyticalVariableCardContextValue => {
  const context = useContext(AnalyticalVariableCardContext);
  if (!context) {
    throw new Error(
      'useAnalyticalVariableCardContext must be used within an AnalyticalVariableCardProvider'
    );
  }
  return context;
};

interface AnalyticalVariableCardProviderProps {
  analyticalVariable: AnalyticalVariable;
  conceptGroups: ConceptGroupOption[];
  isEditable?: boolean;
  onAnalyticalVariableChange?: (updates: Partial<AnalyticalVariable>) => void;
  onDrillDownClick?: (analyticalVariableId: string) => void;
  children: ReactNode;
}

export const AnalyticalVariableCardProvider = ({
  analyticalVariable,
  conceptGroups,
  isEditable = false,
  onAnalyticalVariableChange,
  onDrillDownClick,
  children,
}: AnalyticalVariableCardProviderProps) => {
  const conceptGroupsByDomain = useMemo<ConceptGroupsByDomain>(() => {
    const map = new Map<string, ConceptGroupOption[]>();
    for (const cg of conceptGroups) {
      if (!cg.domains) continue;
      for (const raw of cg.domains.split(/,\s*/)) {
        const domain = raw.trim().toLowerCase();
        if (!domain) continue;
        const list = map.get(domain);
        if (list) {
          list.push(cg);
        } else {
          map.set(domain, [cg]);
        }
      }
    }
    return map;
  }, [conceptGroups]);

  const value = useMemo(
    () => ({
      analyticalVariable,
      conceptGroups,
      conceptGroupsByDomain,
      isEditable,
      onAnalyticalVariableChange,
      onDrillDownClick,
    }),
    [
      analyticalVariable,
      conceptGroups,
      conceptGroupsByDomain,
      isEditable,
      onAnalyticalVariableChange,
      onDrillDownClick,
    ]
  );

  return (
    <AnalyticalVariableCardContext.Provider value={value}>
      {children}
    </AnalyticalVariableCardContext.Provider>
  );
};
